import { useEffect, useState, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';

/**
 * Full-screen success feedback shown after a successful clock-in or clock-out.
 *
 * Displays an animated checkmark, the user's name and time, and a friendly
 * message before auto-dismissing back to the idle screen.
 */
export default function KioskSuccessScreen({
  successData,
  selectedUser,
  goToIdle,
  autoReturnSeconds = 4,
}) {
  const [countdown, setCountdown] = useState(autoReturnSeconds);
  const goToIdleRef = useRef(goToIdle);
  goToIdleRef.current = goToIdle;

  const type = successData?.type;
  const isClockIn = type === 'clock_in';
  const isBreakStart = type === 'break_start';
  const isBreakEnd = type === 'break_end';
  const isBreak = isBreakStart || isBreakEnd;

  const userName = selectedUser
    ? (selectedUser.display_name ||
       `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() ||
       'Unknown')
    : '';
  const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const duration = successData?.duration_minutes
    ? `${Math.floor(successData.duration_minutes / 60)}h ${successData.duration_minutes % 60}m`
    : null;

  // -------------------------------------------------------------------
  // Countdown timer
  // -------------------------------------------------------------------
  useEffect(() => {
    if (countdown <= 0) {
      goToIdleRef.current?.();
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  // -------------------------------------------------------------------
  // Gradient backgrounds
  // -------------------------------------------------------------------
  const breakDuration = successData?.duration_minutes
    ? `${successData.duration_minutes}m`
    : null;

  const bgClass = isBreakStart
    ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600'
    : isBreakEnd
    ? 'bg-gradient-to-br from-blue-500 via-cyan-600 to-blue-700'
    : isClockIn
    ? 'bg-gradient-to-br from-emerald-500 via-green-600 to-emerald-700'
    : 'bg-gradient-to-br from-indigo-500 via-blue-600 to-indigo-700';

  return (
    <motion.div
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${bgClass} select-none`}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Animated checkmark */}
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <svg viewBox="0 0 100 100" className="h-32 w-32">
          <motion.circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="white"
            strokeWidth="3"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
          />
          <motion.path
            d="M30 50 L45 65 L70 35"
            fill="none"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.4, delay: 0.4, ease: 'easeOut' }}
          />
        </svg>
      </motion.div>

      {/* Title */}
      <motion.h1
        className="mt-8 text-4xl font-bold text-white"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        {isBreakStart ? 'Break Started!' : isBreakEnd ? 'Break Ended!' : isClockIn ? 'Clocked In!' : 'Clocked Out!'}
      </motion.h1>

      {/* User name */}
      <motion.p
        className="mt-4 text-2xl font-medium text-white/90"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.45 }}
      >
        {userName}
      </motion.p>

      {/* Time */}
      <motion.p
        className="mt-1 text-xl text-white/80"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.55 }}
      >
        {time}
      </motion.p>

      {/* Duration (clock-out only) or friendly message (clock-in) */}
      <motion.p
        className="mt-6 text-xl font-medium text-white/90"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.65 }}
      >
        {isBreakStart
          ? 'Enjoy your break!'
          : isBreakEnd
          ? breakDuration ? `Break duration: ${breakDuration}` : 'Break ended'
          : isClockIn
          ? 'Have a great day!'
          : duration ? `Total time: ${duration}` : ''}
      </motion.p>

      {/* Countdown */}
      <motion.p
        className="mt-8 text-lg text-white/60"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.8 }}
      >
        Returning in {countdown}s...
      </motion.p>
    </motion.div>
  );
}
